/**
 * Functions and types to determine the containment of geometry within other kinds of geometry.
 * @module
 */
import { DIST_EPSILON, DIST_EPSILON_SQUARED, ZERO_EPSILON } from "./core";
import { polyLine2Area, polyLine2ReverseCopy, polyLine2SignedArea } from "./polyLine2";
import type { PolyLine2, PolyLine2MultiPolygon, PolyLine2Polygon } from "./polyLine2Types";
import type { Vec2 } from "./vec2";
import { vec2Cross, vec2DistSquared, vec2IsBetween } from "./vec2";

const { abs } = Math;

export enum Vec2PolyLine2ContainmentClass {
  INSIDE,
  OUTSIDE,
  COINCIDENT,
}

export function classifyContainmentVec2PolyLine2(
  x: number,
  y: number,
  polygon: PolyLine2,
  tol: number = ZERO_EPSILON,
  dotTol: number = 0,
): Vec2PolyLine2ContainmentClass {
  let inside = false;
  for (let i = 0, l = polygon.length - 2; i < l; i += 2) {
    const xi = polygon[i];
    const yi = polygon[i + 1];
    const xj = polygon[i + 2];
    const yj = polygon[i + 3];

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }

    if (vec2IsBetween(xi, yi, xj, yj, x, y, tol, dotTol)) {
      return Vec2PolyLine2ContainmentClass.COINCIDENT;
    }
  }

  return inside ? Vec2PolyLine2ContainmentClass.INSIDE : Vec2PolyLine2ContainmentClass.OUTSIDE;
}

export enum PolyLine2PolyLine2ContainmentClass {
  INSIDE,
  OUTSIDE,
  INSIDE_COINCIDENT,
  OUTSIDE_COINCIDENT,
  CROSSING,
  COINCIDENT,
  UNKNOWN,
}

export function classifyContainmentPolyLine2PolyLine2(
  test: PolyLine2,
  pl: PolyLine2,
): PolyLine2PolyLine2ContainmentClass {
  let inside = false;
  let outside = false;
  let coincident = false;

  for (let i = 0, l = test.length; i < l; i += 2) {
    const x = test[i];
    const y = test[i + 1];

    switch (classifyContainmentVec2PolyLine2(x, y, pl)) {
      case Vec2PolyLine2ContainmentClass.INSIDE: {
        inside = true;
        break;
      }
      case Vec2PolyLine2ContainmentClass.OUTSIDE: {
        outside = true;
        break;
      }
      case Vec2PolyLine2ContainmentClass.COINCIDENT: {
        coincident = true;
        break;
      }
    }
  }

  if (inside && outside) {
    return PolyLine2PolyLine2ContainmentClass.CROSSING;
  }

  if (inside && !outside) {
    return coincident
      ? PolyLine2PolyLine2ContainmentClass.INSIDE_COINCIDENT
      : PolyLine2PolyLine2ContainmentClass.INSIDE;
  }

  if (outside && !inside) {
    return coincident
      ? PolyLine2PolyLine2ContainmentClass.OUTSIDE_COINCIDENT
      : PolyLine2PolyLine2ContainmentClass.OUTSIDE;
  }

  if (coincident) {
    // compare polygon areas
    const testArea = polyLine2Area(test);
    const plArea = polyLine2Area(pl);

    const areaDiff = testArea - plArea;

    if (abs(areaDiff) < 1e-6) {
      return PolyLine2PolyLine2ContainmentClass.COINCIDENT;
    }

    if (areaDiff < 0) {
      return PolyLine2PolyLine2ContainmentClass.INSIDE_COINCIDENT;
    }

    return PolyLine2PolyLine2ContainmentClass.OUTSIDE_COINCIDENT;
  }

  return PolyLine2PolyLine2ContainmentClass.UNKNOWN;
}

export function classifyContainmentLoopLoop(
  loops: PolyLine2[],
  i: number,
  j: number,
): PolyLine2PolyLine2ContainmentClass {
  return classifyContainmentPolyLine2PolyLine2(loops[i], loops[j]);
}

export enum PolyLine2Containment {
  OUTSIDE,
  INSIDE,
  COINCIDENT,
}

// this function does _not_ handle coincidences where a point is on an edge or where a loop is intersecting another
// loop
export function classifyContainmentPolyLine2PolyLine2Fast(test: PolyLine2, polygon: PolyLine2): PolyLine2Containment {
  let inside = true;
  for (let i = 0, l = test.length; i < l; i += 2) {
    const x = test[i];
    const y = test[i + 1];

    if (!classifyContainmentVec2PolyLine2Fast(x, y, polygon)) {
      inside = false;
      break;
    }
  }

  if (inside) {
    return PolyLine2Containment.INSIDE;
  }

  const px = test[0];
  const py = test[1];

  // check coincidence
  for (let i = 0, l = polygon.length; i < l; i += 2) {
    const x = polygon[i];
    const y = polygon[i + 1];

    if (vec2DistSquared(x, y, px, py) < DIST_EPSILON_SQUARED) {
      return PolyLine2Containment.COINCIDENT;
    }
  }

  return PolyLine2Containment.OUTSIDE;
}

export function classifyContainmentLoopLoopFast(loops: PolyLine2[], i: number, j: number): PolyLine2Containment {
  return classifyContainmentPolyLine2PolyLine2Fast(loops[i], loops[j]);
}

export function classifyContainmentVec2PolyLine2Fast(x: number, y: number, polygon: PolyLine2) {
  const v2 = polygon.length / 2;

  let inside = false;
  for (let i = 0, j = v2 - 1, l = v2; i < l; j = i++) {
    const i2 = i * 2;
    const j2 = j * 2;
    const xi = polygon[i2];
    const yi = polygon[i2 + 1];
    const xj = polygon[j2];
    const yj = polygon[j2 + 1];

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) {
      inside = !inside;
    }
  }

  return inside;
}

export function classifyContainmentVec2PolyLine2PolygonFast(x: number, y: number, polygon: PolyLine2Polygon): boolean {
  if (!classifyContainmentVec2PolyLine2Fast(x, y, polygon[0])) {
    return false;
  }

  for (let i = 1, l = polygon.length; i < l; i++) {
    if (classifyContainmentVec2PolyLine2Fast(x, y, polygon[i])) {
      return false;
    }
  }

  return true;
}

export function classifyContainmentVec2PolyLine2MultiPolygonFast(
  x: number,
  y: number,
  polygons: PolyLine2MultiPolygon,
): boolean {
  for (const polygon of polygons) {
    if (classifyContainmentVec2PolyLine2PolygonFast(x, y, polygon)) {
      return true;
    }
  }
  return false;
}

export function classifyContainmentVec2Polygon(
  x: number,
  y: number,
  polygon: PolyLine2Polygon,
  tol?: number,
): Vec2PolyLine2ContainmentClass {
  if (polygon.length === 0) {
    return Vec2PolyLine2ContainmentClass.OUTSIDE;
  }

  const outerLoop = polygon[0];
  const outerLoopContainment = classifyContainmentVec2PolyLine2(x, y, outerLoop, tol);

  if (outerLoopContainment !== Vec2PolyLine2ContainmentClass.INSIDE || polygon.length === 1) {
    return outerLoopContainment;
  }

  for (let i = 1; i < polygon.length; i++) {
    const loopContainment = classifyContainmentVec2PolyLine2(x, y, polygon[i], tol);
    if (loopContainment === Vec2PolyLine2ContainmentClass.COINCIDENT) {
      return Vec2PolyLine2ContainmentClass.COINCIDENT;
    }

    if (loopContainment === Vec2PolyLine2ContainmentClass.INSIDE) {
      return Vec2PolyLine2ContainmentClass.OUTSIDE;
    }
  }

  return Vec2PolyLine2ContainmentClass.INSIDE;
}

/**
 * Finds a point guaranteed to be inside a simple polygon, even for concave shapes.
 *
 * For each vertex, casts a ray that avoids all other vertices and counts
 * intersections with non-incident edges. If the count is odd the ray enters the
 * interior; if even, the opposite direction is used.
 */
export function getPointInPolygon(polygon: PolyLine2): Vec2 | null {
  const n = (polygon.length - 2) / 2;
  if (n < 3) {
    return null;
  }

  const OFFSET = DIST_EPSILON * 100;

  for (let vi = 0; vi < n; vi++) {
    const vx = polygon[vi * 2];
    const vy = polygon[vi * 2 + 1];

    const dir = findNonVertexRayDirection(polygon, vi, n);
    if (!dir) {
      continue;
    }

    const [dirX, dirY] = dir;

    const prevI = (vi + n - 1) % n;
    let crossings = 0;

    for (let ei = 0; ei < n; ei++) {
      if (ei === vi || ei === prevI) {
        continue;
      }

      const e0x = polygon[ei * 2];
      const e0y = polygon[ei * 2 + 1];
      const e1i = (ei + 1) % n;
      const e1x = polygon[e1i * 2];
      const e1y = polygon[e1i * 2 + 1];

      const ex = e1x - e0x;
      const ey = e1y - e0y;
      const ox = e0x - vx;
      const oy = e0y - vy;

      const crossDE = dirX * ey - dirY * ex;
      if (abs(crossDE) < ZERO_EPSILON) {
        continue;
      }

      const t = (ox * ey - oy * ex) / crossDE;
      if (t <= ZERO_EPSILON) {
        continue;
      }

      const s = (ox * dirY - oy * dirX) / crossDE;
      if (s >= -ZERO_EPSILON && s <= 1 + ZERO_EPSILON) {
        crossings++;
      }
    }

    if (crossings % 2 === 1) {
      return [vx + OFFSET * dirX, vy + OFFSET * dirY];
    } else {
      return [vx - OFFSET * dirX, vy - OFFSET * dirY];
    }
  }

  return null;
}

/**
 * Find a ray direction from vertex vi that does not pass through any other vertex.
 * Collects the angles from vi to every other vertex, sorts them, and picks the
 * midpoint of the widest angular gap.
 */
function findNonVertexRayDirection(polygon: PolyLine2, vi: number, n: number): [number, number] | null {
  const vx = polygon[vi * 2];
  const vy = polygon[vi * 2 + 1];

  const angles: number[] = [];
  for (let vj = 0; vj < n; vj++) {
    if (vj === vi) {
      continue;
    }
    const dx = polygon[vj * 2] - vx;
    const dy = polygon[vj * 2 + 1] - vy;
    if (abs(dx) < DIST_EPSILON && abs(dy) < DIST_EPSILON) {
      continue;
    }
    angles.push(Math.atan2(dy, dx));
  }

  if (angles.length === 0) {
    return null;
  }

  angles.sort((a, b) => a - b);

  let bestAngle = angles[0] + Math.PI;
  let maxGap = 0;

  for (let i = 0; i < angles.length; i++) {
    const next = i + 1 < angles.length ? angles[i + 1] : angles[0] + 2 * Math.PI;
    const gap = next - angles[i];
    if (gap > maxGap) {
      maxGap = gap;
      bestAngle = (angles[i] + next) / 2;
    }
  }

  return [Math.cos(bestAngle), Math.sin(bestAngle)];
}

/**
 * Finds an interior point by iterating consecutive vertex triples of a closed
 * polygon and returning the centroid of the first convex-angle triangle whose
 * centroid lies inside the polygon. The polygon is normalized to CCW winding
 * so that convex vertices have a positive cross product.
 *
 * Every simple polygon has at least one convex vertex whose ear-triangle
 * centroid is interior, so this should always succeed for valid input.
 */
export function computeConvexVertexInteriorPoint(polygon: PolyLine2): Vec2 {
  const ccwPolygon = polyLine2SignedArea(polygon) > 0 ? polyLine2ReverseCopy(polygon) : polygon;
  const vertexCount = (ccwPolygon.length - 2) / 2;
  for (let i = 0; i < vertexCount; i++) {
    const i0 = i * 2;
    const i1 = ((i + 1) % vertexCount) * 2;
    const i2 = ((i + 2) % vertexCount) * 2;

    const ax = ccwPolygon[i1] - ccwPolygon[i0];
    const ay = ccwPolygon[i1 + 1] - ccwPolygon[i0 + 1];
    const bx = ccwPolygon[i2] - ccwPolygon[i1];
    const by = ccwPolygon[i2 + 1] - ccwPolygon[i1 + 1];

    if (vec2Cross(ax, ay, bx, by) <= 0) {
      continue;
    }

    const cx = (ccwPolygon[i0] + ccwPolygon[i1] + ccwPolygon[i2]) / 3;
    const cy = (ccwPolygon[i0 + 1] + ccwPolygon[i1 + 1] + ccwPolygon[i2 + 1]) / 3;
    if (classifyContainmentVec2PolyLine2Fast(cx, cy, polygon)) {
      return [cx, cy];
    }
  }

  throw new Error("computeConvexVertexInteriorPoint: failed to find an interior point for polygon");
}
